<script lang="ts">
  /**
   * A project rendered as a fixed-size card on the board canvas.
   *
   * Deliberately its own component rather than a reuse of ProjectCard: the board
   * card shows a live, scrollable task list and is expected to diverge from the
   * timeline/focus cards as the Boards feature grows.
   *
   * Task rows intentionally mirror ProjectRail's — same priority-coloured
   * checkbox, same completed/won't-do treatment, same "⋯" actions menu — so a
   * task reads and behaves the same wherever it appears.
   */
  import { NodeResizer, type NodeProps } from '@xyflow/svelte';
  import type { Project, Task, TaskPriority } from '../../lib/types';
  import { categoryIcons } from '../../lib/utils';
  import { subscribeToCachedTasks, fetchProjectTasks } from '../../lib/stores/projectTasksCache';
  import { completeTask } from '../../lib/taskApi';
  import { openProjectRail } from '../../lib/stores/ui';
  import { getBoardActions, getBoardDisplay, getBoardTaskUi } from '../../lib/boardContext';

  const display = getBoardDisplay();
  const actions = getBoardActions();
  const taskUi = getBoardTaskUi();

  type Props = NodeProps & {
    data: {
      project: Project;
    };
  };

  let { id, data, selected, width, height }: Props = $props();

  let project = $derived(data.project);

  let tasks = $state<Task[]>([]);
  let tasksLoading = $state(false);
  let tasksFetched = $state(false);

  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: projectTasksCache pub/sub, re-subscribes on project id change, returns cleanup
  $effect(() => {
    const projectId = project.id;
    return subscribeToCachedTasks(projectId, (cached) => {
      tasks = cached.tasks;
      tasksLoading = cached.loading;
      tasksFetched = cached.fetched;
    });
  });

  // The board page batch-fetches on load; this covers cards added afterwards.
  // eslint-disable-next-line momentium/no-unguarded-effect -- conditional data fetch: read-only trigger, writes only to the cache store
  $effect(() => {
    if (!tasksFetched && !tasksLoading) {
      fetchProjectTasks(project.id);
    }
  });

  const priorityOrder: Record<TaskPriority, number> = { high: 0, medium: 1, low: 2, none: 3 };

  let incompleteTasks = $derived(
    tasks
      .filter((t) => t.status === 'todo')
      .sort((a, b) => {
        const diff = priorityOrder[a.priority] - priorityOrder[b.priority];
        return diff !== 0 ? diff : a.position - b.position;
      }),
  );

  let completedTasks = $derived(
    tasks
      .filter((t) => t.status !== 'todo')
      .sort((a, b) => {
        const aTime = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const bTime = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        return bTime - aTime;
      }),
  );

  // At low zoom the task rows become visual noise. Switch to an identity-only
  // marker: the emoji and project name remain, while task detail disappears.
  const SUMMARY_ZOOM_THRESHOLD = 0.65;
  let summaryMode = $derived(display.zoom < SUMMARY_ZOOM_THRESHOLD);
  let headerScale = $derived(Math.max(1, 1 / Math.max(display.zoom, 0.2)));
  // Slightly reduce the screen-space title at extreme zoom-out so more of the
  // name can wrap inside the small card instead of becoming a single character.
  let summaryTitleSize = $derived(display.zoom < 0.35 ? 0.75 : display.zoom < 0.5 ? 0.875 : 1);

  // The toolbar toggle sets the board-wide default; a card keeps its own choice
  // once the user has expanded or collapsed that card specifically.
  let localShowCompleted = $state<boolean | null>(null);
  let showCompleted = $derived(localShowCompleted ?? display.showCompleted);

  let doneCount = $derived(completedTasks.length);
  let totalCount = $derived(tasks.length);
  let progressPct = $derived(totalCount === 0 ? 0 : Math.round((doneCount / totalCount) * 100));

  // Priority is conveyed by the checkbox outline, matching ProjectRail.
  const priorityBorderColors: Record<TaskPriority, string> = {
    none: '#d1d5db',
    low: '#60a5fa',
    medium: '#fb923c',
    high: '#ef4444',
  };

  const priorityHoverBgs: Record<TaskPriority, string> = {
    none: 'rgba(209, 213, 219, 0.12)',
    low: 'rgba(96, 165, 250, 0.08)',
    medium: 'rgba(251, 146, 60, 0.08)',
    high: 'rgba(239, 68, 68, 0.08)',
  };

  async function toggleTask(task: Task) {
    await completeTask(project.id, task.id, tasks);
  }

  function openDetail(task: Task, event: MouseEvent) {
    taskUi.openDetail(task, project.id, event.currentTarget as HTMLElement);
  }

  function toggleMenu(task: Task, event: MouseEvent) {
    event.stopPropagation();
    taskUi.toggleMenu(task, project.id, event.currentTarget as HTMLElement);
  }

  function openProjectDetail() {
    openProjectRail(project.id);
  }
</script>

{#snippet taskRow(task: Task, completed: boolean)}
  <div class="task-item" class:completed class:menu-open={taskUi.openMenuTaskId === task.id}>
    <button
      type="button"
      class="task-checkbox nodrag"
      class:checked={completed}
      class:wont-do={task.status === 'wont_do'}
      style="--cb-border: {priorityBorderColors[task.priority]}; --cb-hover-bg: {priorityHoverBgs[
        task.priority
      ]};"
      onclick={() => toggleTask(task)}
      title={task.status === 'wont_do'
        ? "Won't do — click to reopen"
        : completed
          ? 'Done — click to reopen'
          : 'Mark done'}
    >
      {#if task.status === 'wont_do'}
        <span class="checkbox-glyph">✕</span>
      {:else if completed}
        <span class="checkbox-glyph">✓</span>
      {/if}
    </button>

    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <span
      class="task-title nodrag"
      class:wont-do-text={task.status === 'wont_do'}
      onclick={(e) => openDetail(task, e)}
      role="button"
      tabindex="0"
      title={task.title}>{task.title}</span
    >

    {#if task.notes}
      <span class="notes-indicator" title="Has notes">
        <svg viewBox="0 0 16 16" fill="currentColor">
          <path
            d="M4 1h8a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1zm1 3v1h6V4H5zm0 3v1h6V7H5zm0 3v1h4v-1H5z"
          />
        </svg>
      </span>
    {/if}

    <button
      type="button"
      class="task-menu-btn nodrag hover-reveal"
      onclick={(e) => toggleMenu(task, e)}
      title="Task actions"
      aria-label="Actions for {task.title}">⋯</button
    >
  </div>
{/snippet}

<NodeResizer
  isVisible={selected}
  minWidth={200}
  minHeight={160}
  color="#2563eb"
  onResizeStart={() => actions.onResizeStart?.(id)}
  onResizeEnd={(_event, params) => actions.onResizeEnd(id, params)}
/>

<!-- svelte-ignore a11y_no_static_element_interactions -- canvas node: mouse-driven card whose summary mode double-click opens the project rail; the equivalent accessible action is the header's ↗ button -->
<div
  class="board-card"
  class:selected
  class:summary={summaryMode}
  style="width: {width ? `${width}px` : '260px'}; height: {height
    ? `${height}px`
    : '256px'}; --header-scale: {headerScale}; --card-flow-width: {width ??
    260}px; --card-flow-height: {height ?? 256}px; --summary-title-size: {summaryTitleSize}rem;"
  data-status={project.status}
  data-zoom-mode={summaryMode ? 'summary' : 'detailed'}
  ondblclick={summaryMode ? openProjectDetail : undefined}
>
  <!--
    Header doubles as the drag handle; controls inside opt out via .nodrag.
    Double-clicking it opens the project rail, but only in detailed mode — in
    summary mode the whole card is the target, handled on the card root above.
  -->
  <div class="card-header" ondblclick={summaryMode ? undefined : openProjectDetail}>
    <div class="card-header-content">
      {#if summaryMode}
        <span class="card-title card-summary-title" title={project.title}>
          {categoryIcons[project.category] ?? '📋'}
          {project.title}
        </span>
      {:else}
        <span class="card-icon">{categoryIcons[project.category] ?? '📋'}</span>
        <span class="card-title" title={project.title}>{project.title}</span>
      {/if}
      <button
        class="card-open nodrag"
        type="button"
        onclick={openProjectDetail}
        title="Open project details"
        aria-label="Open {project.title} details"
      >
        ↗
      </button>
    </div>
  </div>

  {#if !summaryMode}
    <div class="card-meta">
      <span class="card-status">{project.status}</span>
      {#if totalCount > 0}
        <span class="card-count">{doneCount}/{totalCount}</span>
      {/if}
    </div>

    {#if totalCount > 0}
      <div class="card-progress">
        <div class="card-progress-fill" style="width: {progressPct}%"></div>
      </div>
    {/if}

    <!--
      `nowheel` stops the canvas from zooming when the pointer scrolls this list.
      Without it the task list is unusable on a zoomable canvas.
    -->
    <div class="card-tasks nowheel">
      {#if tasksLoading && !tasksFetched}
        <div class="card-empty">Loading tasks…</div>
      {:else if totalCount === 0}
        <div class="card-empty">No tasks yet</div>
      {:else}
        {#each incompleteTasks as task (task.id)}
          {@render taskRow(task, false)}
        {/each}

        {#if incompleteTasks.length === 0}
          <div class="card-empty">All tasks done 🎉</div>
        {/if}

        {#if completedTasks.length > 0}
          <button
            type="button"
            class="completed-toggle nodrag"
            onclick={() => (localShowCompleted = !showCompleted)}
          >
            <span class="toggle-arrow" class:open={showCompleted}>▶</span>
            Completed ({completedTasks.length})
          </button>

          {#if showCompleted}
            {#each completedTasks as task (task.id)}
              {@render taskRow(task, true)}
            {/each}
          {/if}
        {/if}
      {/if}
    </div>
  {/if}
</div>

<style>
  .board-card {
    display: flex;
    flex-direction: column;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    font-size: 0.75rem;
    box-sizing: border-box;
  }

  /*
   * At low zoom the card body becomes a compact project marker. The header is
   * inverse-scaled so its screen-space text stays readable while its visual
   * width is still clamped to the card bounds.
   */
  .board-card.summary {
    position: relative;
    border-top-width: 3px;
  }

  .board-card.summary[data-status='planned'] {
    border-top-color: #a78bfa;
  }

  .board-card.summary[data-status='active'] {
    border-top-color: #34d399;
  }

  .board-card.summary[data-status='done'] {
    border-top-color: #9ca3af;
  }

  .board-card.summary::before {
    position: absolute;
    inset: 0 0 auto;
    z-index: 2;
    pointer-events: none;
  }

  .board-card.summary .card-header {
    position: absolute;
    inset: 0;
    min-height: 0;
  }

  .board-card.summary .card-header-content {
    width: calc(var(--card-flow-width) / var(--header-scale));
    height: calc(var(--card-flow-height) / var(--header-scale));
    min-height: 0;
    background: white;
    border: none;
    transform: scale(var(--header-scale));
    transform-origin: top left;
    overflow: hidden;
  }

  .board-card.summary .card-title {
    font-size: var(--summary-title-size);
    line-height: 1.15;
    white-space: normal;
    overflow-wrap: anywhere;
    text-overflow: clip;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 6;
    line-clamp: 6;
  }

  .board-card.summary .card-summary-title {
    text-align: center;
  }

  .board-card.summary .card-icon {
    font-size: 1.25rem;
    margin-top: 0.0625rem;
  }

  .board-card.summary .card-open {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    opacity: 0;
    pointer-events: none;
  }

  .board-card.summary:hover .card-open,
  .board-card.summary.selected .card-open,
  .board-card.summary .card-open:focus-visible {
    opacity: 1;
    pointer-events: auto;
  }

  .board-card.selected {
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
  }

  /* Status accent along the top edge */
  .board-card::before {
    content: '';
    display: block;
    height: 3px;
    flex-shrink: 0;
    background: #9ca3af;
  }
  .board-card[data-status='planned']::before {
    background: #a78bfa;
  }
  .board-card[data-status='active']::before {
    background: #34d399;
  }
  .board-card[data-status='done']::before {
    background: #9ca3af;
  }

  .card-header {
    cursor: grab;
    min-height: 3.25rem;
    user-select: none;
    -webkit-user-select: none;
  }

  .card-header-content {
    display: flex;
    align-items: center;
    background: #f8fafc;
    border-bottom: 1px solid #e5e7eb;
    gap: 0.375rem;
    min-height: 3.25rem;
    box-sizing: border-box;
    padding: 0.5rem 0.625rem;
  }

  .card-icon {
    font-size: 0.8125rem;
    line-height: 1;
    flex-shrink: 0;
  }

  .card-title {
    font-size: 0.9375rem;
    font-weight: 700;
    color: #1f2937;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    line-height: 1.15;
    flex: 1;
    min-width: 0;
  }

  .card-open {
    flex-shrink: 0;
    border: none;
    background: none;
    color: #9ca3af;
    cursor: pointer;
    font-size: 0.75rem;
    padding: 0 0.125rem;
    line-height: 1;
  }

  .card-open:hover {
    color: #2563eb;
  }

  .card-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0 0.625rem 0.375rem;
    font-size: 0.625rem;
    color: #6b7280;
  }

  .card-status {
    text-transform: capitalize;
  }

  .card-count {
    font-variant-numeric: tabular-nums;
  }

  .card-progress {
    height: 2px;
    background: #f3f4f6;
    margin: 0 0.625rem 0.375rem;
    border-radius: 1px;
    overflow: hidden;
  }

  .card-progress-fill {
    height: 100%;
    background: #34d399;
  }

  .card-tasks {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 0 0.375rem 0.375rem;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  /* Slim scrollbar so a scrolling list doesn't dominate a small card */
  .card-tasks {
    scrollbar-width: thin;
    scrollbar-color: #d1d5db transparent;
  }

  .card-tasks::-webkit-scrollbar {
    width: 6px;
  }

  .card-tasks::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }

  .card-tasks::-webkit-scrollbar-track {
    background: transparent;
  }

  .card-empty {
    padding: 0.5rem 0.25rem;
    color: #9ca3af;
    font-size: 0.6875rem;
  }

  .task-item {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.25rem;
    border-radius: 0.25rem;
    transition: background 0.1s;
  }

  .task-item:hover {
    background: rgba(0, 0, 0, 0.03);
  }

  .task-item.completed > .task-title {
    color: #9ca3af;
  }

  .task-item.completed > .task-checkbox,
  .task-item.completed > .notes-indicator {
    opacity: 0.5;
  }

  .task-checkbox {
    width: 0.875rem;
    height: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid var(--cb-border, #d1d5db);
    border-radius: 0.1875rem;
    background: white;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s;
    padding: 0;
  }

  .task-checkbox:hover {
    background: var(--cb-hover-bg, rgba(209, 213, 219, 0.12));
  }

  .task-checkbox.checked {
    background: #3b82f6;
    border-color: #3b82f6;
  }

  .task-checkbox.wont-do {
    background: #9ca3af;
    border-color: #9ca3af;
  }

  .checkbox-glyph {
    font-size: 0.5625rem;
    color: white;
    font-weight: 700;
    line-height: 1;
  }

  .task-title {
    flex: 1;
    font-size: 0.6875rem;
    color: #1f2937;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    padding: 0.0625rem 0.25rem;
    border-radius: 0.125rem;
    transition: background 0.1s;
  }

  .task-title:hover {
    background: rgba(59, 130, 246, 0.08);
  }

  .task-title.wont-do-text {
    text-decoration: line-through;
    color: #9ca3af;
  }

  .notes-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 0.75rem;
    height: 0.75rem;
    color: #9ca3af;
    flex-shrink: 0;
  }

  .notes-indicator svg {
    width: 100%;
    height: 100%;
  }

  .task-menu-btn {
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 0.6875rem;
    font-family: inherit;
    color: #9ca3af;
    background: none;
    border: none;
    border-radius: 0.125rem;
    cursor: pointer;
    opacity: 0.35;
    transition: all 0.15s;
  }

  .task-item:hover .task-menu-btn,
  .task-item.menu-open .task-menu-btn {
    opacity: 1;
    color: #6b7280;
  }

  .task-menu-btn:hover {
    color: #374151;
    background: rgba(0, 0, 0, 0.08);
  }

  .task-menu-btn:focus-visible {
    opacity: 1;
    color: #374151;
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  .completed-toggle {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    padding: 0.375rem 0.25rem;
    margin-top: 0.25rem;
    font-size: 0.625rem;
    font-weight: 600;
    font-family: inherit;
    color: #6b7280;
    background: none;
    border: none;
    border-top: 1px solid #e5e7eb;
    cursor: pointer;
    text-align: left;
    transition: color 0.15s;
  }

  .completed-toggle:hover {
    color: #374151;
  }

  .toggle-arrow {
    font-size: 0.5rem;
    transition: transform 0.15s;
  }

  .toggle-arrow.open {
    transform: rotate(90deg);
  }
</style>
